import { UserEvents } from './UserEvents';
import { unitID, CODE_AGAINST_PROD } from './constants';
import { Socket } from 'socket.io-client';
import { WorldPlayerUnit } from './PlayerUnit';
import { BuildingType, GameUpdateDelta, MessageType, PlayerData, UnitType, SHOW_ALL_ORE, PlayerBuilding } from '../shared/MessageType';
import { GameObjectFactory3D } from './GameObjectFactory';
import { Building3D, Ore3D } from './interfaces3d';
import { Vector3 } from 'babylonjs';

let io: Socket = (window as any).io;

type WorldCallbacks = {
    onResult: (result: any) => void;
    add: {
        existing: (unit: any) => void;
    },
    serverUnits: { [key: number]: any[] | undefined; };
    serverBuildings: { [key: number]: any[] | undefined; };
    otherPlayerColours: { [key: number]: string; };
    renderAttack: (playerId: number, sourceUnit: any, attackedUnit: any) => void;
    renderSparks: (playerId: number, unit: any, ore: any) => void;
    processSyncQueue: () => void;
    queueConsoleMessage?: (message: string, color?: string) => void;
} & any;

const getConnectionDetails = () => {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const playerId = urlParams.get('playerId');
    const password = urlParams.get('password');

    return {
        playerId: (window as any).futureTerminalPlayerId || playerId || '',
        password: (window as any).futureTerminalPassword || password || '',
    };
};

// Connection status tracking
type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';
let connectionStatus: ConnectionStatus = 'connecting';
let connectionDetails: string = 'Connecting...';
let reconnectAttempts: number = 0;

// Update connection status badge
const updateConnectionStatus = (status: ConnectionStatus, details?: string) => {
    connectionStatus = status;
    if (details !== undefined) {
        connectionDetails = details;
    }

    const statusElement = document.getElementById('connection-status');
    const tooltipElement = document.getElementById('connection-tooltip');

    if (statusElement) {
        // Remove all status classes
        statusElement.classList.remove('status-connecting', 'status-connected', 'status-disconnected');
        // Add current status class
        statusElement.classList.add(`status-${status}`);
    }

    if (tooltipElement) {
        tooltipElement.textContent = connectionDetails;
        statusElement?.setAttribute('title', connectionDetails);
    }
};

const getRealSocket = () => {
    try {
        // localhost port 3000, other nothing
        const ehProd = CODE_AGAINST_PROD ? 'https://www.futureterminal.com' : 'http://localhost:3000/';
        const url = window.location.hostname === 'localhost' ? ehProd : '/';
        return (io as any)(url, { transports: ['websocket'] });
    } catch (error) {
        console.error('Error connecting to server', error);
        updateConnectionStatus('disconnected', `Connection error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return null;
    }
};

const getMockSocket = () => {
    return {
        on: (_: string, __: (msg: string) => void) => {
            // console.log(`Listening for ${event}`);
        },
        emit: (_: string, __: string) => {
            // console.log(`Emitting ${event} : ${msg}`);
        }
    }
};

const local = false;
const getSocket = local ? getMockSocket : getRealSocket;

// Add this before connectToServer
let gameObjectFactory: GameObjectFactory3D = null;

// Export function to allow setting the factory
export const setGameObjectFactory = (factory: GameObjectFactory3D) => {
    gameObjectFactory = factory;
};

export const connectToServer = (world: WorldCallbacks) => {
    if (!gameObjectFactory.addExisting) {
        gameObjectFactory.addExisting = (obj: any) => {
            world.add.existing(obj);
        };
    }

    // Initialize connection status
    updateConnectionStatus('connecting', 'Connecting to server...');

    let socket = getSocket();
    let worldPlayerId = -1;

    // Handle connection errors
    if (socket) {
        socket.on('connect', () => {
            reconnectAttempts = 0;
            updateConnectionStatus('connected', 'Connected');
        });

        socket.on('disconnect', (reason: string) => {
            if (reason === 'io server disconnect') {
                updateConnectionStatus('disconnected', `Disconnected by server`);
            } else {
                updateConnectionStatus('disconnected', `Disconnected: ${reason}`);
            }
        });

        socket.on('connect_error', (error: Error) => {
            updateConnectionStatus('disconnected', `Connection error: ${error.message}`);
        });

        socket.on('reconnect_attempt', (attemptNumber: number) => {
            reconnectAttempts = attemptNumber;
            updateConnectionStatus('connecting', `Reconnecting... (attempt ${attemptNumber})`);
        });

        socket.on('reconnect', (attemptNumber: number) => {
            reconnectAttempts = 0;
            updateConnectionStatus('connected', `Reconnected after ${attemptNumber} attempts`);
        });

        socket.on('reconnect_failed', () => {
            updateConnectionStatus('disconnected', 'Reconnection failed. Please refresh.');
        });
    } else {
        // Socket creation failed
        updateConnectionStatus('disconnected', 'Failed to create socket connection');
    }

    // Queues for incremental sync processing
    type SyncUnitTask = {
        playerId: number;
        unit: any;
    };
    type SyncBuildingTask = {
        playerId: number;
        building: any;
    };
    type SyncUnitsTask = {
        playerId: number;
        units: any[];
        list: WorldPlayerUnit[];
        processedCount: number;
    };
    type SyncBuildingsTask = {
        playerId: number;
        buildings: any[];
        list: Building3D[];
        processedCount: number;
    };
    type SyncOresTask = {
        ores: any[];
        processedCount: number;
    };

    const unitSyncQueue: SyncUnitTask[] = [];
    const buildingSyncQueue: SyncBuildingTask[] = [];
    const unitsSyncQueue: SyncUnitsTask[] = [];
    const buildingsSyncQueue: SyncBuildingsTask[] = [];
    const oresSyncQueue: SyncOresTask[] = [];

    // Helper function to process a batch of ores (updates/creates only)
    const processOresBatch = (world: WorldCallbacks, oresBatch: any[]) => {
        if (!world.ores) {
            return;
        }
        const that = world as any;

        oresBatch.forEach(ore => {
            const existingOre = world.ores.find((o: any) => o.id === ore.id);

            if (existingOre) {
                // Update existing ore
                existingOre.ore = ore.ore;
                // Scale the mesh based on remaining ore
                const scale = Math.max(0.3, ore.ore / 100);
                if (existingOre.mesh) {
                    existingOre.mesh.scaling = new Vector3(scale, scale, scale);
                }
                if (that.scene && existingOre.mesh) {
                    const animation = that.scene.beginAnimation(existingOre.mesh, 0, 60, true);
                    const randomFrame = Math.floor(Math.random() * 60);
                    animation.goToFrame(randomFrame);
                }
            } else {
                // Create new ore - this requires access to baseMeshes and COORDINATE_SCALE
                if (that.baseMeshes && that.baseMeshes.ore && that.COORDINATE_SCALE) {
                    const oreInstance = that.baseMeshes.ore.createInstance("ore_" + ore.id);

                    oreInstance.position = new Vector3(0, 0, 0);

                    // Align ore to planet surface
                    if (that.planet) {
                        const planetCentre = that.planet.position;
                        oreInstance.lookAt(planetCentre, 0, -Math.PI / 2, 0);
                    }

                    world.ores.push({
                        mesh: oreInstance,
                        id: ore.id,
                        x: ore.x / that.COORDINATE_SCALE,
                        y: ore.y / that.COORDINATE_SCALE,
                        ore: ore.ore
                    });
                }
            }
        });
    };

    // Helper function to handle ore removals
    const handleOreRemovals = (world: WorldCallbacks, newOres: any[]) => {
        if (!world.ores) {
            return;
        }

        const oresToRemove = world.ores.filter(
            (existingOre: any) => !newOres.find((newOre: any) => newOre.id === existingOre.id)
        );

        oresToRemove.forEach((ore: any) => {
            if (ore.mesh) {
                ore.mesh.dispose();
            }
            const index = world.ores.indexOf(ore);
            if (index !== -1) {
                world.ores.splice(index, 1);
            }
        });
    };

    // Helper function to update fog of war for ores
    const updateOresFogOfWar = (world: WorldCallbacks) => {
        if (!world.ores || !world.fogOfWar) {
            return;
        }
        const that = world as any;

        world.ores.forEach((ore: any) => {
            if (that.COORDINATE_SCALE) {
                // Convert scaled coordinates back to world coordinates for fog check
                const worldX = ore.x * that.COORDINATE_SCALE;
                const worldY = ore.y * that.COORDINATE_SCALE;
                const visibility = world.fogOfWar.getVisibility(worldX, worldY);
                // Hide ores in unexplored areas
                if (ore.mesh) {
                    ore.mesh.isVisible = visibility !== 0 || SHOW_ALL_ORE; // 0 = UNEXPLORED
                }
            }
        });
    };

    const processSyncQueue = () => {
        if (unitsSyncQueue.length > 0) {
            const task = unitsSyncQueue[0];
            const totalUnits = task.units.length;
            const remainingUnits = totalUnits - task.processedCount;
            const unitsToProcess = remainingUnits;
            const endIndex = Math.min(task.processedCount + unitsToProcess, totalUnits);

            for (let i = task.processedCount; i < endIndex; i++) {
                ensureUnit(task.playerId, task.list, task.units[i]);
            }

            task.processedCount = endIndex;

            // If all units are processed, handle removals and remove task
            if (task.processedCount >= totalUnits) {
                const indicesToRemove = task.list.filter(x => !task.units.some(y => y.id === x.id)).map((x, i) => i);
                indicesToRemove.reverse();
                for (let i = 0; i < indicesToRemove.length; i++) {
                    const unitRemovedViaSync = task.list.splice(indicesToRemove[i], 1)[0];
                    (unitRemovedViaSync as any).destroy();
                }
                unitsSyncQueue.shift();
            }
        }

        if (buildingsSyncQueue.length > 0) {
            const task = buildingsSyncQueue[0];
            const totalBuildings = task.buildings.length;
            const remainingBuildings = totalBuildings - task.processedCount;
            const buildingsToProcess = remainingBuildings;
            const endIndex = Math.min(task.processedCount + buildingsToProcess, totalBuildings);

            for (let i = task.processedCount; i < endIndex; i++) {
                ensureBuilding(task.playerId, task.list, task.buildings[i]);
            }

            task.processedCount = endIndex;

            // If all buildings are processed, handle removals and remove task
            if (task.processedCount >= totalBuildings) {
                const indicesToRemove = task.list.filter(x => !task.buildings.some(y => y.id === x.id)).map((x, i) => i);
                indicesToRemove.reverse();
                for (let i = 0; i < indicesToRemove.length; i++) {
                    const otherBuilding = task.list[indicesToRemove[i]];
                    (otherBuilding as { destroy: () => void }).destroy();
                    task.list.splice(indicesToRemove[i], 1);
                }
                buildingsSyncQueue.shift();
            }
        }

        if (unitSyncQueue.length > 0) {
            const unitsToProcess = unitSyncQueue.length;
            for (let i = 0; i < unitsToProcess && unitSyncQueue.length > 0; i++) {
                const task = unitSyncQueue.shift();
                if (!world.serverUnits[task.playerId]) {
                    world.serverUnits[task.playerId] = [];
                }
                const list = world.serverUnits[task.playerId] as WorldPlayerUnit[];
                ensureUnit(task.playerId, list, task.unit, false);
            }
        }

        if (buildingSyncQueue.length > 0) {
            const buildingsToProcess = buildingSyncQueue.length;
            for (let i = 0; i < buildingsToProcess && buildingSyncQueue.length > 0; i++) {
                const task = buildingSyncQueue.shift();
                if (!world.serverBuildings[task.playerId]) {
                    world.serverBuildings[task.playerId] = [];
                }
                const list = world.serverBuildings[task.playerId] as Building3D[];
                ensureBuilding(task.playerId, list, task.building);
            }
        }

        if (oresSyncQueue.length > 0) {
            const task = oresSyncQueue[0];
            const totalOres = task.ores.length;
            const remainingOres = totalOres - task.processedCount;
            const oresToProcess = remainingOres;
            const endIndex = Math.min(task.processedCount + oresToProcess, totalOres);

            // Process the batch of ores incrementally
            const oresBatch = task.ores.slice(task.processedCount, endIndex);
            processOresBatch(world, oresBatch);

            task.processedCount = endIndex;

            // If all ores are processed, handle removals and remove task
            if (task.processedCount >= totalOres) {
                // Handle removals by comparing current ores with the new full array
                handleOreRemovals(world, task.ores);
                // Update fog of war for all ores
                updateOresFogOfWar(world);
                oresSyncQueue.shift();
            }
        }
    };

    // Set up the world callback to process sync queue
    world.processSyncQueue = processSyncQueue;

    socket.on(UserEvents.GotTicks, (ticks: any) => {
        world.tickCallback(ticks);
    });
    socket.on(UserEvents.GotTerrain, (msg) => {
        // console.log(`Received terrain`);
        world.terrainCallback(msg);
    });
    socket.on(UserEvents.GotOres, (msg) => {
        // Queue the ores for incremental processing
        oresSyncQueue.push({
            ores: [...msg], // Create a copy to avoid mutation
            processedCount: 0
        });
    });
    // Helper function to queue console message for batched processing in render loop
    const queueConsoleMessage = (message: string, color?: string) => {
        // Use world's queue method if available (for batched updates in render loop)
        // Otherwise fall back to direct console update (for backwards compatibility)
        if (world.queueConsoleMessage) {
            world.queueConsoleMessage(message, color);
        } else {
            // Fallback: direct console update (less performant)
            const gameConsole = document.getElementById('console');
            if (gameConsole) {
                const style = color ? `style="color: ${color};"` : '';
                gameConsole.innerHTML += `<p ${style}>${message}</p>`;
                // If height overflow, remove oldest message
                if (gameConsole.scrollHeight > 10 * gameConsole.clientHeight) {
                    gameConsole.removeChild(gameConsole.firstChild);
                }
                // Auto-scroll to bottom
                gameConsole.scrollTop = gameConsole.scrollHeight;
            }
        }
    };

    socket.on(MessageType.Action, (payload) => {
        if (payload.k === 'log') {
            console.log(`Action log: ${payload.message}`);
            queueConsoleMessage(payload.message);
        }
        if (payload.k === 'error') {
            console.error(`Action error: ${payload.message}`);
            queueConsoleMessage(payload.message, 'yellow');
        }
        if (payload.k === 'result') {
            // console.log(`Action result: ${JSON.stringify(payload)}`);
            world.onResult(payload);
        }
        if (payload.k === 'removeUnit') {
            const { playerId, id } = payload;
            // Make sure user exists
            if (!world.serverUnits[playerId]) {
                world.serverUnits[playerId] = [];
            }
            // Get users units
            const list = world.serverUnits[playerId] as WorldPlayerUnit[];
            const unit = list.find((u) => u.id === payload.id);
            if (!unit) {
                return;
            }
            const unitRemoved = list.splice(list.indexOf(unit), 1)[0];
            (unitRemoved as any).destroy();
            // console.log(`Removed unit ${id} for player ${playerId}`);
            // console.log(`Unit was at ${unitRemoved.x} ${unitRemoved.y}`);
        }
        if (payload.k === 'removeBuilding') {
            const { playerId, id } = payload;
            // Make sure user exists
            if (!world.serverBuildings[playerId]) {
                world.serverBuildings[playerId] = [];
            }
            // Get users units
            const list = world.serverBuildings[playerId] as PlayerBuilding[];
            const building = list.find((u) => u.id === payload.id);
            if (!building) {
                return;
            }
            const buildingRemoved = list.splice(list.indexOf(building), 1)[0];
            (buildingRemoved as any).destroy();
            // console.log(`Removed building ${id} for player ${playerId}`);
            // console.log(`Building was at ${buildingRemoved.x} ${buildingRemoved.y}`);
        }
        if (payload.k === 'status') {
            const { playerId, id, status } = payload;
            // Make sure player exists
            if (!world.serverBuildings[playerId]) {
                world.serverBuildings[playerId] = [];
            }
            // Find building and update status
            const list = world.serverBuildings[playerId] as Building3D[];
            const building = list.find((u) => u.id === id);
            if (!building) {
                return;
            }
        }
        if (payload.k === 'doMine') {
            const { playerId, id, oreId } = payload;
            const list = world.serverUnits[playerId] as WorldPlayerUnit[];
            const unit = list?.find((u) => u.id === id);
            if (!unit) {
                return;
            }
            const ore = world.ores.find((o: Ore3D) => o.id === oreId);
            if (!ore) {
                return;
            }
            world.renderSparks(playerId, unit, ore);
        }
        if (payload.k === 'doLoad') {
            const { playerId, id, buildingId } = payload;
            const list = world.serverUnits[playerId] as WorldPlayerUnit[];
            const unit = list?.find((u) => u.id === id);
            if (!unit) {
                return;
            }
            const building = world.serverBuildings[playerId]?.find((o) => o.id === buildingId);
            if (!building) {
                return;
            }
            world.renderSparks(playerId, unit, building);
        }
    });

    socket.on(UserEvents.GotSync, (playerData) => {
        for (let key of Object.keys(playerData)) {
            const userData: PlayerData = playerData[key];
            const colour = userData.colour;
            if (!world.otherPlayerColours[key]) {
                world.otherPlayerColours[key] = colour;
            }
            const units = userData.units;
            syncUnits(key as any as number, units);
            const buildings = userData.buildings;
            syncBuildings(key as any as number, buildings);
            if (userData.resources) {
                world.resourcesCallback(userData.resources);
            }
            if (userData.data) {
                world.dataCallback(userData.data);
            }
        }
    });
    socket.on(MessageType.Move, (payload) => {
        syncUnit(payload.playerId, payload, true);
    });
    socket.on(MessageType.BulkMove, (payload) => {
        for (const message of payload) {
            syncUnit(message.playerId, message, true);
        }
    });
    socket.on(UserEvents.PlayerConnected, (connectionId) => {
        const isSelfConnecting = worldPlayerId === -1;
        const message = `Connected as player id ${connectionId}`;
        const { playerId, password } = getConnectionDetails();
        worldPlayerId = +playerId;
        // console.log(message);
        if (isSelfConnecting) {
            updateConnectionStatus('connected', `Connected as player ${worldPlayerId}`);
            socket.emit(MessageType.PlayerId, worldPlayerId, password);
            world.callback(worldPlayerId, socket);
            console.log(`Sending first sync to ${worldPlayerId}`);
            socket.emit(UserEvents.GetSync, worldPlayerId);
            setInterval(() => {
                console.log(`Sending interval sync to ${worldPlayerId}`);
                socket.emit(UserEvents.GetSync, worldPlayerId);
                socket.emit(UserEvents.GetOres, worldPlayerId);
            }, 5000);
        }
    });
    const renderUnits = (message: any) => {
        let messageType = message.messageType;
        let payload = message.payload;
        let { playerId, playerRace, id, k, x, y } = payload;
        const units = [];
        const buildings = [];
        if (!world || !world.serverUnits) {
            // console.log('World not found, cannot render other units yet');
            return;
        }
        if (!world.otherPlayerRaces[playerId]) {
            world.otherPlayerRaces[playerId] = playerRace;
        }
        if (!playerRace) {
            playerRace = world.otherPlayerRaces[playerId];
            if (!playerRace) {
                // TODO: This is a stop-gap to prevent the game from crashing for now
                playerRace = 'hoomans';
                world.otherPlayerRaces[playerId] = playerRace;
            }
        }
        if (!world.serverUnits[playerId]) {
            world.serverUnits[playerId] = [];
            world.serverBuildings[playerId] = [];
        }

        if (messageType === MessageType.SpawnBuilding) {
            buildings.push({
                id: unitID(),
                buildingType: payload.buildingType,
                x,
                y
            });
        }

        const buildingList = world.serverBuildings[playerId];
        for (let i = 0; i < buildings.length; i++) {
            const building = buildings[i];
            const existing = buildingList.find(u => u.x === building.x && u.y === building.y);
            if (existing) {
                existing.id = building.id;
                existing.life = building.life;
            } else {
                let tint = 0xcc33cc;
                // If it is my building, change the tint to green
                if (+playerId === worldPlayerId) {
                    tint = 0x00aa00;
                }
                let newBuilding: Building3D = null;
                if (building.k === BuildingType.House) {
                    newBuilding = gameObjectFactory.getHouse(world, building.x, building.y);
                } else {
                    newBuilding = gameObjectFactory.getHub(world, building.x, building.y);
                }
                newBuilding.id = building.id;
                newBuilding.playerId = playerId;
                buildingList.push(newBuilding);
                gameObjectFactory.addExisting(newBuilding);
            }
        }

        if (messageType === MessageType.SpawnWorker) {
            units.push({
                id: id,
                k: UnitType.Worker,
                x,
                y,
                tx: x,
                ty: y
            });
        }

        if (messageType === MessageType.SpawnTank) {
            units.push({
                id: id,
                k: UnitType.Tank,
                x,
                y,
                tx: x,
                ty: y
            });
        }

        if (messageType === MessageType.SpawnUnit) {
            units.push({
                id: id,
                k,
                x,
                y,
                tx: x,
                ty: y
            });
        }

        const list = world.serverUnits[playerId] as WorldPlayerUnit[];
        if (messageType === MessageType.Move) {
            const existing = list.find(u => u.id === id) as WorldPlayerUnit;
            if (existing) {
                // EXPERIMENT: Other units are lerping towards their target
                // existing.x = unit.x;
                // existing.y = unit.y;
                if (playerId !== worldPlayerId) {
                    if (existing.x !== existing.tx || existing.y !== existing.ty) {
                        existing.x = existing.tx;
                        existing.y = existing.ty;
                    }
                    existing.tx = x;
                    existing.ty = y;
                }
            }
        }
        for (let i = 0; i < units.length; i++) {
            const unit = units[i];
            const existing = list.find(u => u.id === unit.id) as WorldPlayerUnit;
            if (existing) {
                existing.playerId = playerId;
                // EXPERIMENT: Other units are lerping towards their target
                // existing.x = unit.x;
                // existing.y = unit.y;
                if (playerId !== worldPlayerId) {
                    existing.tx = unit.x;
                    existing.ty = unit.y;
                    existing.life = unit.life;
                }
            } else {
                if (unit.k === UnitType.Worker) {
                    const worker = gameObjectFactory.getWorker(world, unit.x, unit.y, 0xff0000, 1, playerId);
                    worker.playerId = playerId;
                    worker.k = unit.k;
                    worker.id = unit.id;
                    worker.tx = unit.tx;
                    worker.ty = unit.ty;
                    list.push(worker);
                    gameObjectFactory.addExisting(worker);
                } else if (unit.k === UnitType.Tank) {
                    const tank = gameObjectFactory.getTank(world, unit.k, unit.x, unit.y, 0xff0000, 1, playerId);
                    tank.playerId = playerId;
                    tank.id = unit.id;
                    tank.tx = unit.tx;
                    tank.ty = unit.ty;
                    list.push(tank);
                    gameObjectFactory.addExisting(tank);
                } else if (unit.k === UnitType.Crawler) {
                    const crawler = gameObjectFactory.getCrawler(world, unit.x, unit.y, 0xff0000, 1, playerId);
                    crawler.playerId = playerId;
                    crawler.id = unit.id;
                    crawler.tx = unit.tx;
                    crawler.ty = unit.ty;
                    list.push(crawler);
                    gameObjectFactory.addExisting(crawler);
                } else if (unit.k === UnitType.Artillery) {
                    const artillery = gameObjectFactory.getArtillery(world, unit.x, unit.y, playerId);
                    artillery.playerId = playerId;
                    artillery.id = unit.id;
                    artillery.tx = unit.tx;
                    artillery.ty = unit.ty;
                    list.push(artillery);
                    gameObjectFactory.addExisting(artillery);
                } else if (unit.k === UnitType.ScoutPlane) {
                    const scoutPlane = gameObjectFactory.getScoutPlane(world, unit.x, unit.y, playerId);
                    scoutPlane.playerId = playerId;
                    scoutPlane.id = unit.id;
                    scoutPlane.tx = unit.tx;
                    scoutPlane.ty = unit.ty;
                    list.push(scoutPlane);
                    gameObjectFactory.addExisting(scoutPlane);
                } else {
                    // oops
                    console.error(`Unknown unit type: ${unit.k}`);
                }
            }
        }
    };
    socket.on(MessageType.BulkAttack, (payload: any[]) => {
        for (const attackMessage of payload) {
            const { playerId, id, damage, targetPlayerId, targetId } = attackMessage;
            if (!world) {
                // console.log('World not found, cannot handle attack');
                continue;
            }
            let attacker = world.serverUnits[playerId]?.find((u: any) => u.id === id);
            let isCannon = false;
            if (!attacker) {
                attacker = world.serverBuildings[playerId]?.find((u: any) => u.id === id);
                isCannon = true;
            }
            if (!attacker) {
                continue;
            }
            let defender = world.serverUnits[targetPlayerId]?.find((x: any) => x.id === targetId);
            if (!defender) {
                defender = world.serverBuildings[targetPlayerId]?.find((x: any) => x.id === targetId);
            }
            if (!defender) {
                continue;
            }

            // console.log(`Attacking ${defender.id} with ${attacker.id}`);
            world.renderAttack(playerId, attacker, defender, isCannon);
            defender.life -= damage;
        }
    });

    socket.on(MessageType.GameUpdateDelta, (delta: GameUpdateDelta) => {
        if (!world) {
            return;
        }

        // Process moves
        if (delta.moves) {
            for (const move of delta.moves) {
                syncUnit(move.playerId, move, true);
            }
        }

        // Process attacks
        if (delta.attacks) {
            for (const attackMessage of delta.attacks) {
                const { playerId, id, damage, targetPlayerId, targetId } = attackMessage;
                let attacker = world.serverUnits[playerId]?.find((u: any) => u.id === id);
                let isCannon = false;
                if (!attacker) {
                    attacker = world.serverBuildings[playerId]?.find((u: any) => u.id === id);
                    isCannon = true;
                }
                if (!attacker) {
                    continue;
                }
                let defender = world.serverUnits[targetPlayerId]?.find((x: any) => x.id === targetId);
                if (!defender) {
                    defender = world.serverBuildings[targetPlayerId]?.find((x: any) => x.id === targetId);
                }
                if (!defender) {
                    continue;
                }

                world.renderAttack(playerId, attacker, defender, isCannon);
                defender.life -= damage;
            }
        }

        // Process unit spawns
        if (delta.spawnUnits) {
            for (const spawnUnit of delta.spawnUnits) {
                const { playerId, id, k, x, y, tx, ty, life, w, s, gx, gy } = spawnUnit;
                const unit = {
                    id,
                    k,
                    x,
                    y,
                    tx: tx ?? x,
                    ty: ty ?? y,
                    life: life ?? 100,
                    w,
                    s,
                    gx,
                    gy
                };
                renderUnits({ messageType: MessageType.SpawnUnit, payload: { playerId, ...unit } });
            }
        }

        // Process building spawns
        if (delta.spawnBuildings) {
            for (const spawnBuilding of delta.spawnBuildings) {
                const { playerId, id, k, x, y, life } = spawnBuilding;
                renderUnits({ messageType: MessageType.SpawnBuilding, payload: { playerId, id, k, x, y, life: life ?? 1000 } });
            }
        }

        // Process actions
        if (delta.actions) {
            for (const action of delta.actions) {
                const { playerId, id, k, targetId, buildingId, oreId, status } = action;
                if (k === 'removeUnit') {
                    if (id !== undefined) {
                        const unitList = world.serverUnits[playerId];
                        if (unitList) {
                            const index = unitList.findIndex((u: any) => u.id === id);
                            if (index !== -1) {
                                const unit = unitList[index];
                                (unit as any).destroy();
                                unitList.splice(index, 1);
                            }
                        }
                    }
                } else if (k === 'removeBuilding') {
                    if (id !== undefined) {
                        const buildingList = world.serverBuildings[playerId];
                        if (buildingList) {
                            const index = buildingList.findIndex((b: any) => b.id === id);
                            if (index !== -1) {
                                const building = buildingList[index];
                                (building as any).destroy();
                                buildingList.splice(index, 1);
                            }
                        }
                    }
                } else if (k === 'doMine') {
                    if (id !== undefined && oreId !== undefined) {
                        world.renderSparks(playerId, world.serverUnits[playerId]?.find((u: any) => u.id === id), world.ores?.find((o: any) => o.id === oreId));
                    }
                } else if (k === 'doLoad') {
                    // Handle load action if needed
                } else if (k === 'status') {
                    if (id !== undefined && status !== undefined) {
                        const building = world.serverBuildings[playerId]?.find((b: any) => b.id === id);
                        if (building) {
                            building.status = status;
                        }
                    }
                }
            }
        }

        // Process unit stance changes
        if (delta.setUnitStance) {
            for (const stanceChange of delta.setUnitStance) {
                const { playerId, id, stance } = stanceChange;
                const unit = world.serverUnits[playerId]?.find((u: any) => u.id === id);
                if (unit) {
                    unit.s = stance;
                }
            }
        }
    });

    let messageCount = 0;
    // messageTypes.forEach((messageType) => {
    // socket.on(messageType, (payload) => handle(messageType, payload));
    // });
    const handle = (messageType: MessageType, payload) => {
        messageCount++;
        if (payload === 'restart') {
            // Reload window
            window.location.reload();
        }
        world.queueRender(messageType, payload, renderUnits);
        // console.log(`Received message ${messageCount} : ${msg}`);
        // const serverstatus = document.getElementById('serverstatus');
        // serverstatus.innerHTML = msg;
        // (socket.auth as any).serverOffset = serverOffset;
    };

    const ensureUnit = (playerId: number, list: WorldPlayerUnit[], unit: any, updateOnly = false) => {
        let { id, k, x, y, tx, ty, ore, s, gx, gy } = unit;
        let isAbsolute = tx === undefined && ty === undefined;
        const existing = list.find(u => u.id === id) as WorldPlayerUnit;
        if (existing) {
            existing.s = s;
            existing.ax = x;
            existing.ay = y;
            if (gx !== undefined) {
                (existing as any).gx = gx;
            } else {
                delete (existing as any).gx;
            }
            if (gy !== undefined) {
                (existing as any).gy = gy;
            } else {
                delete (existing as any).gy;
            }

            if (isAbsolute) {
                existing.x = x;
                existing.y = y;
            }

            const predictiveContinueInSameDirection = false;
            if (predictiveContinueInSameDirection) {
                // Use vx and vy to predict where the unit will be
                if (existing.tx === undefined || existing.ty === undefined) {
                    existing.tx = existing.x;
                    existing.ty = existing.y;
                }
                const angle = Math.atan2(y - existing.y, x - existing.x);
                existing.tx = x + Math.cos(angle) * existing.vx * 60;
                existing.ty = y + Math.sin(angle) * existing.vy * 60;
            } else {
                existing.tx = tx || x;
                existing.ty = ty || y;
            }

            if (existing.k === 1) {
                if (ore !== undefined) {
                    (existing as any).ore = ore;
                }
            }
        } else if (!updateOnly) {
            let unitToAdd: WorldPlayerUnit = null;

            if (k === UnitType.Worker) {
                unitToAdd = getWorkerForPlayer(world, unit, playerId);
                if (ore !== undefined) {
                    (unitToAdd as WorldPlayerUnit).ore = ore;
                }
            } else if (k === UnitType.Tank || k === UnitType.Archer || k === UnitType.Crawler || k === UnitType.Artillery) {
                if (k === UnitType.Artillery) {
                    unitToAdd = getArtilleryForPlayer(world, unit, playerId);
                } else if (k === UnitType.Archer) {
                    unitToAdd = getTankForPlayer(world, unit, playerId);
                    (unitToAdd as any).scale = 0.8;
                    (unitToAdd as any).damage = 4;
                } else if (k === UnitType.Crawler) {
                    unitToAdd = getCrawlerForPlayer(world, unit, playerId);
                } else {
                    unitToAdd = getTankForPlayer(world, unit, playerId);
                }
            } else if (k === UnitType.ScoutPlane) {
                unitToAdd = getScoutPlaneForPlayer(world, unit, playerId);
            }

            if (!unitToAdd) {
                return;
            }

            unitToAdd.playerId = playerId;
            unitToAdd.k = k;
            unitToAdd.id = id;
            unitToAdd.x = x;
            unitToAdd.y = y;
            unitToAdd.tx = x;
            unitToAdd.ty = y;
            unitToAdd.vx = 0;
            unitToAdd.vy = 0;
            unitToAdd.s = s;
            if (gx !== undefined) (unitToAdd as any).gx = gx;
            if (gy !== undefined) (unitToAdd as any).gy = gy;

            // Set tint to reddish for other players, green otherwise
            if (+playerId === worldPlayerId) {
                (unitToAdd as any).fillColor = 0x33cc33;
            } else {
                (unitToAdd as any).fillColor = 0xcc3333;
            }
            (unitToAdd as any).init();
            list.push(unitToAdd);
            gameObjectFactory.addExisting(unitToAdd);
        }
    }

    const syncUnit = (playerId: number, unit: any, updateOnly = false) => {
        // Make sure user exists
        if (!world.serverUnits[playerId]) {
            world.serverUnits[playerId] = [];
        }
        // Get users units
        const list = world.serverUnits[playerId] as WorldPlayerUnit[];
        ensureUnit(playerId, list, unit, updateOnly);
    }

    const syncUnits = (playerId: number, units: any) => {
        // Make sure user exists
        if (!world.serverUnits[playerId]) {
            world.serverUnits[playerId] = [];
        }
        // Get users units
        const list = world.serverUnits[playerId] as WorldPlayerUnit[];
        // Queue the sync work instead of processing immediately
        unitsSyncQueue.push({
            playerId,
            units: [...units], // Create a copy to avoid mutation
            list,
            processedCount: 0
        });
    }

    const ensureBuilding = (playerId: number, list: Building3D[], building: any) => {
        const { id, k: buildingType, x, y, life, ore } = building;
        const existing = list.find(u => u.id === id) as Building3D;
        if (existing) {
            existing.x = x;
            existing.y = y;
            existing.life = life;
            if (existing.k === 1) {
                if (ore !== undefined) {
                    (existing as any).ore = ore;
                }
            }
        } else {
            let newBuilding: Building3D = null;

            if (buildingType === BuildingType.House) {
                newBuilding = gameObjectFactory.getHouse(world, x, y);
            } else if (buildingType === BuildingType.Cannon) {
                newBuilding = gameObjectFactory.getCannon(world, x, y);
            } else if (buildingType === BuildingType.LaserCannon) {
                newBuilding = gameObjectFactory.getLaserCannon(world, x, y);
            } else if (buildingType === BuildingType.MissileCannon) {
                newBuilding = gameObjectFactory.getMissileCannon(world, x, y);
            } else if (buildingType === BuildingType.Hub) {
                newBuilding = gameObjectFactory.getHub(world, x, y);
            } else if (buildingType === BuildingType.OreProcessor) {
                newBuilding = gameObjectFactory.getOreProcessor(world, x, y);
            } else if (buildingType === BuildingType.Wall) {
                newBuilding = gameObjectFactory.getWall(world, x, y);
            } else if (buildingType === BuildingType.SolarFarm) {
                newBuilding = gameObjectFactory.getSolarFarm(world, x, y);
            } else {
                return;
            }
            newBuilding.id = id;
            newBuilding.playerId = playerId;
            list.push(newBuilding);
            gameObjectFactory.addExisting(newBuilding);
        }
    }

    const syncBuildings = (playerId: number, buildings: any) => {
        // Make sure user exists
        if (!world.serverBuildings[playerId]) {
            world.serverBuildings[playerId] = [];
        }
        // Get users buildings
        const list = world.serverBuildings[playerId] as Building3D[];
        // Queue the sync work instead of processing immediately
        buildingsSyncQueue.push({
            playerId,
            buildings: [...buildings], // Create a copy to avoid mutation
            list,
            processedCount: 0
        });
    }
};

function getWorkerForPlayer(world: WorldCallbacks, unit: any, playerId: number) {
    const worker = gameObjectFactory.getWorker(world, unit.x, unit.y, 0xff0000, 1, playerId);
    return worker;
}

function getTankForPlayer(world: WorldCallbacks, unit: any, playerId: number) {
    const tank = gameObjectFactory.getTank(world, unit.k, unit.x, unit.y, 0xff0000, 1, playerId);
    return tank;
}

function getCrawlerForPlayer(world: WorldCallbacks, unit: any, playerId: number) {
    const crawler = gameObjectFactory.getCrawler(world, unit.x, unit.y, 0xff0000, 1, playerId);
    return crawler;
}

function getArtilleryForPlayer(world: WorldCallbacks, unit: any, playerId: number) {
    const artillery = gameObjectFactory.getArtillery(world, unit.x, unit.y, playerId);
    return artillery;
}

function getScoutPlaneForPlayer(world: WorldCallbacks, unit: any, playerId: number) {
    const scoutPlane = gameObjectFactory.getScoutPlane(world, unit.x, unit.y, playerId);
    return scoutPlane;
}